import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSales } from '../context/SalesContext';
import DashboardLayout from '../components/DashboardLayout';
import SalesForm from '../components/SalesForm';
import SalesCharts from '../components/SalesCharts';
import BottomSheet from '../components/BottomSheet';
import ListCard from '../components/ui/ListCard';
import { salesService } from '../services';
import { formatCurrency, formatDate } from '../utils/formatters';
import {
    monthKey, listMonths, salesInMonth, salesInYear, sumAmount, topProduct,
    isSameDay, formatMonth, productName
} from '../utils/dashboardStats';

const Dashboard = () => {
    const { createSale } = useSales();
    const [appMessage, setAppMessage] = useState({ text: '', type: '' });
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [allSales, setAllSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(monthKey(new Date()));
    const [params, setParams] = useSearchParams();

    // The tab bar's centre button links here with ?new=sale.
    useEffect(() => {
        if (params.get('new') === 'sale') {
            setIsSheetOpen(true);
            setParams({}, { replace: true });
        }
    }, [params, setParams]);

    // Load every sale once; month and year figures are worked out here
    const loadSales = async () => {
        try {
            setLoadError(false);
            const sales = await salesService.getAll();
            setAllSales(Array.isArray(sales) ? sales : []);
        } catch (error) {
            console.error('Error loading dashboard sales:', error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSales();
    }, []);

    const handleRefresh = async () => {
        await loadSales();
        setAppMessage({ text: 'Data refreshed!', type: 'success' });
        setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
    };

    const handleAddSale = async (formData) => {
        try {
            await createSale(formData);
            await loadSales();
            setIsSheetOpen(false);
            setAppMessage({ text: 'Sale recorded!', type: 'success' });
            setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
        } catch (error) {
            console.error("Failed to add sale:", error);
            setAppMessage({ text: 'Failed to save.', type: 'error' });
        }
    };

    const months = useMemo(() => listMonths(allSales), [allSales]);
    const monthSales = useMemo(() => salesInMonth(allSales, selectedMonth), [allSales, selectedMonth]);
    const monthTotal = useMemo(() => sumAmount(monthSales), [monthSales]);
    const year = Number(selectedMonth.slice(0, 4));
    const yearTop = useMemo(() => topProduct(salesInYear(allSales, year)), [allSales, year]);
    const todaySales = useMemo(
        () => sumAmount(allSales.filter(sale => isSameDay(sale.date || sale.createdAt, new Date()))),
        [allSales]
    );

    return (
        <DashboardLayout>
            <div className="space-y-4 md:space-y-6">
                {/* Header */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <h1 className="text-xl md:text-2xl font-semibold text-slate-900 truncate">
                            {formatMonth(selectedMonth)}
                        </h1>
                        <div className="flex items-center gap-2 shrink-0">
                            <select
                                aria-label="Select month"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="h-9 rounded-full bg-white border border-slate-200 pl-3 pr-8 text-sm font-medium text-slate-700 focus:ring-blue-500"
                            >
                                {months.map((key) => (
                                    <option key={key} value={key}>{formatMonth(key)}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                aria-label="New sale"
                                onClick={() => setIsSheetOpen(true)}
                                className="hidden md:inline-flex items-center h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                            >
                                New sale
                            </button>
                        </div>
                    </div>

                    <div className="flex overflow-x-auto gap-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x hide-scrollbar">
                        {/* Today's sales */}
                        <div className="min-w-[80%] sm:min-w-[300px] snap-center bg-slate-900 rounded-2xl p-5 text-white">
                            <div className="text-sm font-medium text-slate-400">Sales today</div>
                            <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">{formatCurrency(todaySales)}</div>
                            <div className="text-xs text-slate-400 mt-1">
                                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                            </div>
                        </div>

                        {/* Month total */}
                        <div className="min-w-[80%] sm:min-w-[300px] snap-center bg-blue-600 rounded-2xl p-5 text-white">
                            <div className="text-sm font-medium text-blue-100">Total sales for the month</div>
                            <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">{formatCurrency(monthTotal)}</div>
                            <div className="text-xs text-blue-100 mt-1">
                                {formatMonth(selectedMonth)} · {monthSales.length} {monthSales.length === 1 ? 'sale' : 'sales'}
                            </div>
                        </div>

                        {/* Top product of the year */}
                        <div className="min-w-[80%] sm:min-w-[300px] snap-center bg-white border border-slate-200 rounded-2xl p-5 text-slate-900">
                            <div className="text-sm font-medium text-slate-500">Top product in {year}</div>
                            <div className="mt-2 text-lg font-semibold truncate">{yearTop.name}</div>
                            <div className="text-2xl font-semibold text-blue-600 tabular-nums">{formatCurrency(yearTop.amount)}</div>
                            <div className="text-xs text-slate-400 mt-1">so far this year</div>
                        </div>
                    </div>
                </div>

                {/* App message */}
                {appMessage.text && (
                    <div role="status" className={`mx-auto max-w-md p-3 rounded-xl text-center text-sm font-medium ${appMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                        {appMessage.text}
                    </div>
                )}

                <SalesCharts sales={monthSales} />

                {/* Recent transactions */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-base font-semibold text-slate-900">Recent sales</h2>
                        {monthSales.length > 0 && (
                            <Link to="/sales" className="text-sm font-medium text-blue-600">See all</Link>
                        )}
                    </div>

                    {loadError ? (
                        <div className="text-center py-8 bg-red-50 rounded-2xl border border-red-100">
                            <p className="text-sm text-red-700 mb-2">Could not load sales.</p>
                            <button onClick={handleRefresh} className="text-sm font-medium text-red-700 underline">Try again</button>
                        </div>
                    ) : loading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                        </div>
                    ) : monthSales.length === 0 ? (
                        <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-200">
                            <p className="text-sm text-slate-500">No transactions yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {monthSales.slice(0, 5).map((sale) => (
                                <ListCard
                                    key={sale.id || sale._id}
                                    title={sale.customerName}
                                    subtitle={`${productName(sale)} · ${formatDate(sale.date || sale.createdAt)}`}
                                    amount={formatCurrency(sale.amount)}
                                    amountTone="text-emerald-600"
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <BottomSheet
                isOpen={isSheetOpen}
                onClose={() => setIsSheetOpen(false)}
                title="New sale"
            >
                <SalesForm onSubmit={handleAddSale} />
            </BottomSheet>
        </DashboardLayout>
    );
};

export default Dashboard;
